import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, MessageSquare, ClipboardCheck, Users, Calendar, BookOpenCheck, BarChart3, RefreshCw, ShieldCheck, BadgeCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { QuickAnswerCard, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { SOCIAL } from "../data/siteData";
import { HOW_IT_WORKS_AEO } from "../data/aeoContent";

const STEPS = [
    {
        n: "01",
        icon: MessageSquare,
        title: "You enquire — under 2 minutes",
        time: "Day 0 · 2 min",
        body: "Share your child's grade, board, subjects and locality (or 'online'). Tap the demo button, or call +91 7000530316. Mention any preferences — female tutor, time window, specific city / locality.",
        bullets: ["No payment", "No card", "No commitment"],
        accent: "from-blue-500 to-blue-700",
    },
    {
        n: "02",
        icon: ClipboardCheck,
        title: "We match — verified specialist within 24-48 hours",
        time: "Day 0-2 · 24-48 hrs",
        body: "Our matching engine + a human reviewer shortlist 1-3 verified specialist tutors from the curated pool — every one already cleared the 4-step screen (academic credential, subject test, teaching demo, police background check).",
        bullets: ["Curated pool, never marketplace", "~80% applicant rejection", "Locality-aware for J/B/N"],
        accent: "from-indigo-500 to-blue-700",
    },
    {
        n: "03",
        icon: Users,
        title: "Free 1:1 demo — see the tutor teach first",
        time: "Day 1-3 · 60-90 min",
        body: "Your child attends a full 1:1 demo class with the matched tutor — at home (J/B/N) or 1:1 live online (everywhere else). The tutor diagnoses the chapter gap. You watch the chemistry. Approve afterwards.",
        bullets: ["Free 1:1 with the actual matched tutor", "Diagnostic doubles as planning input", "Approve before any payment"],
        accent: "from-amber-500 to-orange-600",
    },
    {
        n: "04",
        icon: BookOpenCheck,
        title: "Personalised plan — chapter by chapter",
        time: "Week 1",
        body: "Based on the diagnostic demo, the tutor builds a written chapter-by-chapter plan calibrated to your child's specific gaps, board, and target outcome. Reviewed weekly. Adaptive pacing.",
        bullets: ["Custom — not templated", "Chapter-by-chapter weak-spot mocks", "Adaptive pacing every session"],
        accent: "from-emerald-500 to-teal-700",
    },
    {
        n: "05",
        icon: Calendar,
        title: "Weekly classes — same tutor entire term",
        time: "Ongoing · Same tutor",
        body: "60-90 minute 1:1 sessions at your home or 1:1 live online — same tutor each week. No rotation. Recordings shared for online classes. Homework allocated each class.",
        bullets: ["Same tutor — full academic year", "Recordings included for online", "Homework + revision included"],
        accent: "from-purple-500 to-fuchsia-700",
    },
    {
        n: "06",
        icon: BarChart3,
        title: "Sunday WhatsApp parent report",
        time: "Every Sunday",
        body: "Every Sunday, a written WhatsApp report from the tutor — topics covered, what was tested, gaps remaining, plan for next week. You always know where your child stands. No dashboard fishing.",
        bullets: ["Written report — not auto-email", "Topics + tests + gaps + plan", "Parent input feeds back into the plan"],
        accent: "from-rose-500 to-pink-700",
    },
    {
        n: "07",
        icon: RefreshCw,
        title: "Right-fit guarantee — replace if mismatch",
        time: "Any time · No cost",
        body: "If the matched tutor isn't a fit after 2-3 sessions — or after any week of the term — we replace at no cost. Per our published Parent Policy. You never lose enrolment, data or progress.",
        bullets: ["Free replacement", "Published policy at /policies/parent", "No silent dissatisfaction"],
        accent: "from-slate-600 to-slate-800",
    },
];

const TRUST_PILLARS = [
    { icon: ShieldCheck, t: "4-step tutor verification", d: "Credentials + subject test + teaching demo + police background — every tutor, no exceptions." },
    { icon: BadgeCheck, t: "Strict 1 tutor : 1 student", d: "Never a batch. Never a 'small group of 4-5'. 60-90 minutes undivided 1:1 attention each session." },
    { icon: Calendar, t: "Same tutor entire term", d: "The tutor at the demo is the tutor through the final exam. No rotation, no surprise replacements." },
    { icon: BarChart3, t: "Weekly written parent report", d: "Sunday WhatsApp report — topics, tests, gaps, plan for next week. Standard, never premium." },
    { icon: RefreshCw, t: "Free tutor replacement", d: "If the fit isn't right, we replace at no cost. Per published Parent Policy." },
    { icon: ShieldCheck, t: "No advance fee · No lock-in", d: "Pay only after the free demo. Monthly billing. Cancel any month with notice. Published refund policy." },
];

export default function HowItWorks() {
    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "HowTo",
                "name": "How to Hire a Verified 1:1 Home Tutor with Anand Tutorials",
                "description": "Step-by-step process to hire a verified 1:1 home tutor in India — enquire, match, free demo, personalised plan, weekly classes, Sunday parent reports, right-fit replacement guarantee.",
                "totalTime": "PT48H",
                "supply": [{ "@type": "HowToSupply", "name": "Child's grade, board, subjects, locality" }],
                "tool": [{ "@type": "HowToTool", "name": "Phone or WhatsApp to +91 7000530316" }],
                "step": STEPS.map((s, i) => ({
                    "@type": "HowToStep",
                    "position": i + 1,
                    "name": s.title,
                    "text": s.body,
                    "url": `https://anandtutorials.com/how-it-works#step-${i + 1}`,
                })),
            },
            {
                "@type": "FAQPage",
                "mainEntity": [
                    {
                        "@type": "Question",
                        "name": "How does Anand Tutorials work end-to-end?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Seven steps: (1) you enquire in under 2 minutes; (2) we match a verified 1:1 specialist tutor within 24-48 hours; (3) you attend a free 1:1 demo; (4) the tutor builds a personalised chapter-by-chapter plan; (5) weekly 1:1 classes begin with the same tutor; (6) you receive a written Sunday WhatsApp parent report; (7) if the tutor isn't a fit, we replace at no cost.",
                        },
                    },
                    {
                        "@type": "Question",
                        "name": "How long does the matching process take?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Most free demos are scheduled within 24-48 hours of enquiry. In Jabalpur, Bhopal and Nagpur we often place a tutor on a same-day demo for enquiries received before noon.",
                        },
                    },
                    {
                        "@type": "Question",
                        "name": "Is the demo class really free?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes — the first 1:1 demo class with the matched tutor is 100% free. No card. No commitment. You only enrol after you've seen the tutor teach your child.",
                        },
                    },
                    {
                        "@type": "Question",
                        "name": "What if the matched tutor isn't a fit?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "If the chemistry isn't right after the demo or within 2-3 sessions, we replace the tutor at no cost. Per our published Parent Policy. You don't lose your enrolment, data, or progress.",
                        },
                    },
                    {
                        "@type": "Question",
                        "name": "Do you offer 1:1 live online classes?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes — direct home tuition in Jabalpur, Bhopal, Nagpur, and 1:1 live online classes pan-India and across 27 international regions. Same tutor across both modes; recordings included for online classes.",
                        },
                    },
                ],
            },
        ],
    };

    return (
        <div data-testid="how-it-works-page">
            <SEO
                title="How Anand Tutorials Works — 7 Steps from Enquiry to Outcome"
                description="Step-by-step: how Anand Tutorials matches a verified 1:1 home tutor for your child. Enquire → match in 24-48 hours → free 1:1 demo → personalised plan → weekly classes → Sunday parent report → free tutor replacement if mismatch. No advance fee. No lock-in."
                keywords="how anand tutorials works, home tuition process india, 1:1 tutoring process, how to hire home tutor india, anand tutorials demo class, anand tutorials parent report, free home tutor demo india, home tuition onboarding process"
                path="/how-it-works"
                schemas={[schema, buildConversationalFAQSchema(HOW_IT_WORKS_AEO.faqs)]}
            />

            <PageHeader
                eyebrow="How it works"
                title="Seven steps. Two days. One free demo."
                subtitle="From your enquiry to your child's first lesson — here's the full operating process. No marketing fluff. The actual cadence, every parent, every term."
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid="hiw-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Start with a Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href="tel:+917000530316" data-testid="hiw-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                </div>
            </PageHeader>

            {/* V65 — AEO Quick-Answer card */}
            <QuickAnswerCard
                question={HOW_IT_WORKS_AEO.quickAnswer.question}
                answer={HOW_IT_WORKS_AEO.quickAnswer.answer}
                bullets={HOW_IT_WORKS_AEO.quickAnswer.bullets}
                testId="how-it-works-quick-answer"
            />

            <div className="container mx-auto px-4 py-12 lg:py-16 max-w-6xl">

                {/* Timeline of 7 steps */}
                <section className="mb-16">
                    <div className="grid md:grid-cols-2 gap-5">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={s.n}
                                    id={`step-${i + 1}`}
                                    data-testid={`hiw-step-${i + 1}`}
                                    className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${s.accent} text-white flex items-center justify-center shrink-0`}>
                                            <Icon className="w-7 h-7" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Step {s.n}</span>
                                                <span className="text-[10px] font-semibold text-emerald-600">· {s.time}</span>
                                            </div>
                                            <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-2 leading-snug">{s.title}</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed mb-3">{s.body}</p>
                                            <ul className="space-y-1">
                                                {s.bullets.map((b, j) => (
                                                    <li key={j} className="text-xs text-slate-700 flex items-start gap-1.5">
                                                        <span className="text-emerald-600 mt-0.5">✓</span>
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Trust pillars */}
                <section className="mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">The six rules we never break</h2>
                    <p className="text-slate-600 mb-6">These are structural — not aspirational. Built into the operating model, every single engagement, since 2013.</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {TRUST_PILLARS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <div key={i} data-testid={`hiw-pillar-${i}`} className="bg-blue-50 border border-blue-100 rounded-xl p-5">
                                    <Icon className="w-7 h-7 text-[#1E3A8A] mb-3" />
                                    <div className="font-heading font-bold text-base text-[#0F172A] mb-1.5">{p.t}</div>
                                    <p className="text-sm text-slate-700 leading-relaxed">{p.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Final CTA */}
                <section data-testid="hiw-cta-section" className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-8 md:p-12 text-center">
                    <h3 className="font-heading text-2xl md:text-4xl font-bold mb-3">Start with Step 01 — under 2 minutes</h3>
                    <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-lg">Tell us your child&apos;s grade, board and subjects. We&apos;ll match a verified 1:1 specialist and confirm the free demo within 24-48 hours.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                            Book a Free 1:1 Demo <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a href={`https://wa.me/${SOCIAL.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 text-white font-semibold px-6 py-3 rounded-lg hover:bg-emerald-600 transition-colors">
                            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                        </a>
                        <a href="tel:+917000530316" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/15">
                            <Phone className="w-4 h-4" /> Call Us
                        </a>
                    </div>
                </section>

            </div>

            {/* V65 — AEO Conversational FAQ */}
            <ConversationalFAQ
                heading="Deeper questions about the Anand Tutorials workflow"
                subheading="Natural-language answers for parents evaluating our onboarding process, matching accuracy, and mid-engagement flexibility."
                items={HOW_IT_WORKS_AEO.faqs}
                testId="how-it-works-conversational-faq"
            />
        </div>
    );
}
